import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { logSentryError } from '@node-space/utils'
import { usePayoutsMutation } from 'hooks/mutations/usePayoutsMutation'
import { usePayoutsV2PostAuthMutation } from 'hooks/mutations/usePayoutsV2Mutation'
import { useWithdrawMutation } from 'hooks/mutations/useWithdrawMutation'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse, BeneficiaryDetails } from 'types/beneficiaries'
import { PayoutsV2PostAuthRequest } from 'types/payoutsV2'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import { SourcePayWallet } from '../../Pay/types'
import { FiatSendFormData, FiatSendFormKeys } from '../form/fiatSendFormSchema'
import { FiatSendFormProps } from '../types'
import { getFirstPartyUnifiedPayoutRequest } from '../utils'

interface UsePayoutSubmitProps {
  fiatBeneficiary: BeneficiaryDetails
  selectedWallet: SourcePayWallet
  formValues: FiatSendFormData
  form: FiatSendFormProps
  setStep: (step: FiatSendStep) => void
}

export const usePayoutSubmit = ({
  fiatBeneficiary,
  selectedWallet,
  formValues,
  form,
  setStep,
}: UsePayoutSubmitProps) => {
  const { t } = useTranslation()
  const { twoFactorEnabled } = useTwoFAContext()
  const queryClient = useQueryClient()
  const [authRequest, setAuthRequest] = useState<PayoutsV2PostAuthRequest>(null)
  const [submitBeneficiary, setSubmitBeneficiary] = useState(false)
  const [calloutError, setCalloutError] = useState(null)
  const isLegacyPaymentMethod = !isNaN(Number(fiatBeneficiary?.reference))
  const isPayoutsV2Enabled =
    !isLegacyPaymentMethod && !!selectedWallet?.supportedTransferDestinations?.length

  const { mutate: withdrawMutation, isPending: isLegacyWithdrawalLoading } = useWithdrawMutation()
  const { mutate: submitPayoutRequest, isPending: isPayoutRequestLoading } = usePayoutsMutation()
  const { mutate: submitPayoutAuthRequest, isPending: isPayoutAuthRequestLoading } =
    usePayoutsV2PostAuthMutation()

  const onSubmitPayoutHandler = () => {
    if (isPayoutsV2Enabled) {
      submitPayout()
    } else {
      withdraw()
    }
  }

  /* Withdraw V1 */
  const withdraw = async () => {
    const beneficiaryIdentifierKey = isLegacyPaymentMethod
      ? 'paymentMethodId'
      : 'beneficiaryReference'

    const withdrawRequest = {
      address: JSON.stringify({ [beneficiaryIdentifierKey]: fiatBeneficiary?.reference }),
      paymentReference: formValues?.paymentReference,
      amount: formValues.amount,
      authorization: {
        ...(twoFactorEnabled && { totp: formValues.twoFactorCode }),
        ...(!twoFactorEnabled && { password: formValues.twoFactorCode }),
      },
    }

    withdrawMutation(
      { walletId: selectedWallet?.legacyWalletId.toString(), requestData: withdrawRequest },
      {
        onSuccess: () => {
          setStep(FiatSendSteps.EOF)
          queryClient.invalidateQueries({
            queryKey: reactQueryKeys.walletTransactions({
              walletId: Number(selectedWallet?.walletId),
              offset: 0,
              max: 10,
            }),
          })
        },
        onError: (error: BaseErrorResponse) => {
          if (error?.data?.errorList?.length) {
            error.data.errorList.forEach(err => {
              form.setError(err?.parameter as FiatSendFormKeys, { message: error?.message })
            })
          } else if (error?.status === 403) {
            form.setError('twoFactorCode', {
              message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
            })
          } else {
            setCalloutError(t('withdrawError'))
            logSentryError('Error from Confirmation - postWithdraw', error, withdrawRequest)
          }
        },
      }
    )
  }

  /* Payouts V2 */
  const submitPayout = async () => {
    setCalloutError(null)
    const payoutsPrerequest = getFirstPartyUnifiedPayoutRequest(
      formValues,
      selectedWallet,
      fiatBeneficiary
    )

    submitPayoutRequest(payoutsPrerequest, {
      onError: onErrorHandler,
    })
  }

  const onErrorHandler = (error: BaseErrorResponse) => {
    if (error?.status === 428) {
      setAuthRequest({
        challenge: error?.data?.challenge?.value,
        tokenResponse: formValues?.twoFactorCode,
        mfaCallbackUrl: error?.data?.callbackUrl,
      })
      form?.setValue('challengeCode', error?.data?.challenge?.value)
      setSubmitBeneficiary(true)
    } else if (error?.data?.errorList) {
      error.data.errorList.forEach(err => {
        form.setError(err?.parameter as FiatSendFormKeys, { message: error?.message })
      })
    } else if (error?.status === 403) {
      form.setError('twoFactorCode', {
        message: twoFactorEnabled ? t('authCodeInvalid') : t('authPasswordInvalid'),
      })
    } else {
      setCalloutError(t('payoutError'))
      logSentryError('Error from Confirmation - postPayPrerequest', error, {
        amount: formValues?.amount,
        reference: formValues?.paymentReference,
        walletId: selectedWallet?.walletId,
        currencyCode: formValues?.currencyCode,
      })
    }
  }

  const onSubmitPostRequest = () => {
    const payoutsPostrequest: PayoutsV2PostAuthRequest = {
      challenge: authRequest?.challenge,
      tokenResponse: authRequest?.tokenResponse,
      mfaCallbackUrl: authRequest?.mfaCallbackUrl,
    }

    submitPayoutAuthRequest(payoutsPostrequest, {
      onSuccess: () => {
        setStep(FiatSendSteps.EOF)
        queryClient.invalidateQueries({
          queryKey: reactQueryKeys.walletTransactions({
            walletId: Number(selectedWallet?.walletId),
            offset: 0,
            max: 10,
          }),
        })
      },
      onError: onErrorHandler,
      onSettled: () => {
        setSubmitBeneficiary(false)
      },
    })
  }

  useEffect(() => {
    submitBeneficiary && onSubmitPostRequest()
  }, [submitBeneficiary])

  return {
    onSubmitPayoutHandler,
    isLegacyWithdrawalLoading,
    isPayoutRequestLoading,
    isPayoutAuthRequestLoading,
    calloutError,
  }
}
